// components/integrations/IntegrationDataMappingDiagram.js
import React, { use, useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Position,
} from "reactflow";

import "reactflow/dist/style.css";
import CustomNode from "./../../../Common/misc/CustomNode";
import RootNode from "./../../../Common/misc/RootNode";

// Import the utility functions
import DataTranslationButtonNode from "./../../../Common/misc/DataTranslationButtonNode";
import DataTranslateNode from "./../../../Common/misc/DataTranslateNode";
import DataTranslationForm from "./../../../Common/misc/DataTranslationForm";

import { createNodesAndEdgesFromSchema, isValidConnection } from "../../../Common/utils/dataMappingUtils";
import { extractPlaceholders, getDynamicSchema } from "@Common/utils/utils";
import { v4 as uuidv4 } from 'uuid';

import { useSelector, useDispatch } from 'react-redux'; // Import useSelector and useDispatch
import { updateIntegrationMappingDetails } from '@/Redux/slices/integrationSlice';



import { parse, visit, Kind } from 'graphql';

const getUniqueTranslationId = () => {
  return uuidv4();
}

const nodeTypes = {
  custom: CustomNode,
  root: RootNode,
  buttonNode: DataTranslationButtonNode,
  translateNode: DataTranslateNode,
};

const DataMappingDiagram = ({ interactive, onMappingsAdded }) => {
  const reactFlowWrapper = useRef(null);
  const { fitView, setCenter, getEdges, getNodes } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const reactFlowInstance = useRef(null);
  const [triggerRequestSchema, setTriggerRequestSchema] = useState(null);
  const [sourceRequestSchema, setSourceRequestSchema] = useState(null);
  const [sourceRequestUrl, setSourceRequestUrl] = useState("")
  const [sourceRequestHeaders, setSourceRequestHeaders] = useState([]);
  const [sourceResponseSchema, setSourceResponseSchema] = useState(null);
  const [destinationRequestSchema, setDestinationRequestSchema] = useState(null);
  const [destinationResponseSchema, setDestinationResponseSchema] = useState(null);
  const [triggerResponseSchema, setTriggerResponseSchema] = useState(null);
  const [dataMappings, setDataMappings] = useState([]);
  const sourceRootNodeX = 800;
  const sourceRequestNodeXOffset = 150;
  const nodeYStart = 10;
  const nodeYIncrement = 60;

  const dispatch = useDispatch();
  const integrationDetails = useSelector((state) => state.integration.selectedIntegration);
  const status = useSelector((state) => state.integration.status);
  const error = useSelector((state) => state.integration.error);



  function generateJsonSchemaFromGraphQLQuery(query) {
    try {
      const parsedQuery = parse(query);
      const operationDefinition = parsedQuery.definitions.find(
        (def) => def.kind === Kind.OPERATION_DEFINITION
      );

      if (!operationDefinition) {
        console.error("No operation definition found in the query.");
        return null;
      }

      const fields = {};
      const required = [];

      const selectionSet = operationDefinition.selectionSet;
      if (selectionSet && selectionSet.selections) {
        selectionSet.selections.forEach((selection) => {
          if (selection.kind === Kind.FIELD) {
            const fieldName = selection.name.value;
            if (selection.selectionSet && selection.selectionSet.selections) {
              // Handle nested fields recursively
              const { nestedFields, nestedRequired } = processNestedFields(selection.selectionSet.selections);
              fields[fieldName] = {
                type: "object",
                properties: nestedFields,
                required: nestedRequired,
              };
            } else {
              // Handle simple fields
              fields[fieldName] = { type: "string" }; // Default type is string
              required.push(fieldName);
            }
          }
        });
      }

      return {
        type: "object",
        properties: fields,
        required: required,
      };
    } catch (error) {
      console.error("Error parsing GraphQL query:", error);
      return null;
    }
  }

  function processNestedFields(selections) {
    const nestedFields = {};
    const nestedRequired = [];

    selections.forEach((nestedSelection) => {
      if (nestedSelection.kind === Kind.FIELD) {
        const nestedFieldName = nestedSelection.name.value;
        if (nestedSelection.selectionSet && nestedSelection.selectionSet.selections) {
          // Recursively handle deeper nested fields
          const { nestedFields: deeperNestedFields, nestedRequired: deeperNestedRequired } = processNestedFields(nestedSelection.selectionSet.selections);
          nestedFields[nestedFieldName] = {
            type: "object",
            properties: deeperNestedFields,
            required: deeperNestedRequired,
          };
        } else {
          nestedFields[nestedFieldName] = { type: "string" }; // Default type is string
          nestedRequired.push(nestedFieldName);
        }
      }
    });

    return { nestedFields, nestedRequired };
  }




  // const extractGraphQLVariables = (query) => {
  //   const variableRegex = /\$\w+:\s*\w+/g;
  //   const variables = {};
  //   let match;

  //   while ((match = variableRegex.exec(query)) !== null) {
  //     const parts = match[0].substring(1).split(':');
  //     const name = parts[0].trim();
  //     const type = parts[1].trim();

  //     variables[name] = {
  //       type: type.toLowerCase(), // Convert to lowercase for JSON schema consistency
  //     };
  //   }

  //   return {
  //     type: "object",
  //     properties: variables,
  //     required: Object.keys(variables),
  //   };
  // }

  // const graphqlQuery = `
  // query GetProducts($priceGreaterThan: Float, $category: String, $searchString: String) {
  //   products(where: {
  //     price_gt: $priceGreaterThan, # Price greater than a given value
  //     category: $category,       # Filter by category
  //     name_contains: $searchString # Filter by name containing a string
  //   }) {
  //     id
  //     name
  //     price
  //     category
  //     description
  //   }
  // }
  // `;


  // function generateJsonSchemaFromFields(fieldsString) {
  //   const fields = fieldsString
  //     .split('\n')
  //     .map((field) => field.trim())
  //     .filter((field) => field !== '');

  //   const properties = {};
  //   const required = [];

  //   fields.forEach((field) => {
  //     if (field.includes('{') && field.includes('}')) {
  //       // Handle nested object
  //       const fieldName = field.split('{')[0].trim();
  //       const innerFieldsString = field
  //         .substring(field.indexOf('{') + 1, field.lastIndexOf('}'))
  //         .trim();
  //       const innerFields = generateJsonSchemaFromFields(innerFieldsString); // Recursive call

  //       properties[fieldName] = {
  //         type: 'object',
  //         properties: innerFields.properties,
  //         required: innerFields.required,
  //       };
  //     } else if (!field.includes('{') && !field.includes('}')) {
  //       // Handle simple field
  //       properties[field] = { type: 'string' };
  //     }
  //     if(!field.includes('{')){
  //       required.push(field.split('{')[0].trim());
  //     }
  //   });

  //   if (properties.price && properties.price.properties && properties.price.properties.value) {
  //     properties.price.properties.value.type = "number"
  //   }

  //   const schema = {
  //     type: 'object',
  //     properties: properties,
  //     required: required.map(field => field.split('{')[0].trim()), // Ensure required array uses base field names
  //   };

  //   return schema;
  // }

  // const fieldsString = `
  // id
  // name
  // price {
  //   value
  //   currency
  // }
  // category
  // description
  // `;


  //   const query = `
  // {
  //   qQueryEndpoint {
  //     id
  //     name
  //     dateOfBirth
  //   }
  // } 
  // `;

  //   // const mutation = `
  //   // mutation {
  //   //   saveSomething {
  //   //     id
  //   //   }
  //   // }
  //   // `;

  //   const firstOperationDefinition = (ast) => ast.definitions[0];
  //   const firstFieldValueNameFromOperation = (operationDefinition) => operationDefinition.selectionSet.selections[0].name.value;



//   useEffect(() => {

//     const mutation = `
//   mutation SaveSomething(
//     $id: ID!
//     $name: String!
//     $dateOfBirth: String
//     $orderDetails: [OrderDetailInput!]
//   ) {
//     saveSomething(
//       id: $id
//       name: $name
//       dateOfBirth: $dateOfBirth
//       orderDetails: $orderDetails
//     ) {
//       id
//       name
//       dateOfBirth
//       orderDetails {
//         itemName
//         qty
//         description
//         manufactured {
//           companyName
//           year
//         }
//       }
//     }
//   }
// `;

//     function extractVariablesFromMutation(mutationString) {
//       const variableRegex = /\$(\w+):\s*(\w+)(!)?/g;
//       const variables = {};
//       let match;

//       while ((match = variableRegex.exec(mutationString)) !== null) {
//         const variableName = match[1];
//         const variableType = match[2];
//         const isRequired = match[3] === "!";

//         variables[variableName] = {
//           type: variableType,
//           required: isRequired,
//         };
//       }

//       return variables;
//     }

//     // Example usage:
//     const extractedVariables = extractVariablesFromMutation(mutation);
//     console.log(JSON.stringify(extractedVariables, null, 2));

//   }, []);











  // only add edges if interactive is true
  const onConnect = useCallback(
    (connection) => {
      if (interactive && isValidConnection(connection)) {
        setEdges((eds) => {
          const newEdges = addEdge(
            {
              ...connection,
              type: "simplebezier",
              animated: true,
              style: { strokeWidth: 1, stroke: "#ffffff" },
            },
            eds
          );

          // Check for translateNode connection completion
          const translateNodeId = connection.source.startsWith("data-translation") ? connection.source : connection.target.startsWith("data-translation") ? connection.target : null;

          if (translateNodeId) {
            const connectedEdges = newEdges.filter(edge => edge.source === translateNodeId || edge.target === translateNodeId);
            if (connectedEdges.length === 2) {
              // Delay the execution to allow React Flow to update
              setTimeout(() => {
                const translateNode = getNodes().find(node => node.id === translateNodeId);
                if (translateNode) {
                  const connectedNodesDetails = updateConnectedNodesDetails(translateNode);
                  setSelectedNode(connectedNodesDetails);
                  setShowSidebar(true);
                }
              }, 0); // Minimal delay
            }
          }

          return newEdges;
        });
      }
    },
    [setEdges, interactive]
  );

  // Use onInit to get the React Flow instance
  const onReactFlowInit = useCallback((_reactFlowInstance) => {
    reactFlowInstance.current = _reactFlowInstance;
  }, []);

  const handleOnDataTranslationClick = useCallback((nodeId) => {
    setTimeout(() => {
      if (reactFlowInstance.current) {
        const allNodes = reactFlowInstance.current.getNodes();
        const clickedNode = allNodes.find((node) => node.id === nodeId);
        if (clickedNode) {
          const connectedNodesDetails = updateConnectedNodesDetails(clickedNode);
          setSelectedNode(connectedNodesDetails);
          setShowSidebar(true);
        } else {
          console.warn(`Node with ID ${nodeId} not found.`);
          setShowSidebar(false);
          setSelectedNode(null);
        }
      }
    }, 0);
  }, []);

  const handleAddDataTranslation = useCallback(() => {
    const newTranslationNodeId = `data-translation-${getUniqueTranslationId()}`;

    const newTranslationNode = {
      id: newTranslationNodeId,
      type: "translateNode",
      data: {
        label: "Data Translation",
        onClick: handleOnDataTranslationClick,
      },
      position: { x: 1250, y: 100 },
    };
    setNodes((nds) => [...nds, newTranslationNode]);
    setCenter(1200, 200, {
      zoom: 1.25,
      duration: 500,
    });
  }, [setNodes, setCenter, fitView, handleOnDataTranslationClick]);

  useEffect(() => {
    if (integrationDetails) {
      try {
        if (integrationDetails.triggerDetails && integrationDetails.triggerDetails.triggerRequestSchema) {
          setTriggerRequestSchema(JSON.parse(integrationDetails.triggerDetails.triggerRequestSchema));
        } else {
          setTriggerRequestSchema(null);
        }

        if (integrationDetails.sourceDetails && integrationDetails.sourceDetails) {
          setSourceRequestUrl(integrationDetails.sourceDetails.restAPISourceUrl);
        }

        if (integrationDetails.sourceDetails && integrationDetails.sourceDetails.restAPISourceRequestSchema) {
          setSourceRequestSchema(JSON.parse(integrationDetails.sourceDetails.restAPISourceRequestSchema));
        } else {
          setSourceRequestSchema(null);
        }

        if (integrationDetails.sourceDetails && integrationDetails.sourceDetails.restAPISourceResponseSchema) {
          setSourceResponseSchema(JSON.parse(integrationDetails.sourceDetails.restAPISourceResponseSchema));
        } else {
          setSourceResponseSchema(null);
        }

        if (integrationDetails.destinationDetails && integrationDetails.destinationDetails.restAPIDestinationRequestSchema) {
          setDestinationRequestSchema(JSON.parse(integrationDetails.destinationDetails.restAPIDestinationRequestSchema));
        } else {
          setDestinationRequestSchema(null);
        }

        if (integrationDetails.destinationDetails && integrationDetails.destinationDetails.restAPIDestinationResponseSchema) {
          setDestinationResponseSchema(JSON.parse(integrationDetails.destinationDetails.restAPIDestinationResponseSchema));
        } else {
          setDestinationResponseSchema(null);
        }

        if (integrationDetails.triggerDetails && integrationDetails.triggerDetails.triggerResponseSchema) {
          setTriggerResponseSchema(JSON.parse(integrationDetails.triggerDetails.triggerResponseSchema));
        } else {
          setTriggerResponseSchema(null);
        }

        if (integrationDetails.mappingDetails) {
          setDataMappings(integrationDetails.mappingDetails);
        }

      } catch (error) {
        console.error("Error parsing schema:", error);
        setTriggerRequestSchema(null);
        setSourceRequestSchema(null);
        setSourceResponseSchema(null);
        setDestinationRequestSchema(null);
        setDestinationResponseSchema(null);
        setTriggerResponseSchema(null);
      }
    }
  }, [integrationDetails]);

  useEffect(() => {
    const newNodes = [];
    const newEdges = [];

    let startX = 10
    // Trigger Root Node
    let triggerRootNode = null;
    let sourceRequestRootNode = null;
    let sourceResponseRootNode = null;
    let destinationRootNode = null;
    let destinationResponseRootNode = null;
    let triggerResponseRootNode = null;


    if (triggerRequestSchema && triggerRequestSchema.properties) {
      triggerRootNode = {
        id: "trigger",
        type: "root",
        data: { label: "Trigger" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false
      };
      newNodes.push(triggerRootNode);
    } else if (triggerRequestSchema && triggerRequestSchema.type === "array") {
      triggerRootNode = {
        id: "trigger",
        type: "root",
        data: { label: "Trigger" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false
      };
      newNodes.push(triggerRootNode);
    }

    // Source Request Root Node
    if ((sourceRequestSchema && sourceRequestSchema.properties) || sourceResponseSchema && sourceResponseSchema.properties) {
      startX += 810;

      sourceRequestRootNode = {
        id: "source-request",
        type: "root",
        data: { label: "Source" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false,
      };
      newNodes.push(sourceRequestRootNode);

      // Source Response Root Node
      sourceResponseRootNode = {
        id: "source-response",
        type: "root",
        data: { label: "Source" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false,
      };
      newNodes.push(sourceResponseRootNode);
    }

    if (destinationRequestSchema && destinationRequestSchema.properties) {
      startX += 1210;
      // Destination Root Node
      destinationRootNode = {
        id: "destination-request",
        type: "root",
        data: { label: "Destination" },
        position: { x: startX, y: nodeYStart },
        targetPosition: Position.Bottom,
        sourcePosition: Position.Right,
        isDraggable: false,
      };
      newNodes.push(destinationRootNode);

      // Destination Root Node
      destinationResponseRootNode = {
        id: "destination-response",
        type: "root",
        data: { label: "Destination" },
        position: { x: startX, y: nodeYStart },
        targetPosition: Position.Bottom,
        sourcePosition: Position.Right,
        isDraggable: false,
      };
      newNodes.push(destinationResponseRootNode);
    }


    // Generate Trigger Request Nodes and Edges
    if (triggerRequestSchema && triggerRequestSchema.properties) {
      const { nodes: newTriggerRequestNodes, edges: newTriggerRequestEdges } =
        createNodesAndEdgesFromSchema(
          triggerRequestSchema,
          "trigger",
          100,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          true
        );
      newNodes.push(...newTriggerRequestNodes);
      newEdges.push(...newTriggerRequestEdges);
    } else if (triggerRequestSchema && triggerRequestSchema.type === "array") {
      const { nodes: newTriggerRequestNodes, edges: newTriggerRequestEdges } =
        createNodesAndEdgesFromSchema(
          triggerRequestSchema.items[0],
          "trigger",
          100,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          true
        );
      newNodes.push(...newTriggerRequestNodes);
      newEdges.push(...newTriggerRequestEdges);
    }

    if (triggerResponseSchema && triggerResponseSchema.properties) {
      startX += 810;
      // Destination Root Node
      triggerResponseRootNode = {
        id: "trigger-response",
        type: "root",
        data: { label: "Response" },
        position: { x: startX, y: nodeYStart },
        targetPosition: Position.Bottom,
        isDraggable: false,
      };
      newNodes.push(triggerResponseRootNode);
    }

    // Generate Source Request Nodes and Edges
    if (sourceRequestSchema && sourceRequestSchema.properties) {
      const { nodes: newSourceRequestNodes, edges: newSourceRequestEdges } =
        createNodesAndEdgesFromSchema(
          sourceRequestSchema,
          "source-request",
          100,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          true,
          sourceRequestRootNode.position.x
        );
      newNodes.push(...newSourceRequestNodes);
      newEdges.push(...newSourceRequestEdges);
    } else if (sourceRequestUrl) {
      const urlPathParams = extractPlaceholders(sourceRequestUrl);
      if (urlPathParams && urlPathParams.length > 0) {
        const sourceRequestSchemaDynamic = JSON.parse(getDynamicSchema(urlPathParams));

        const { nodes: newSourceRequestNodes, edges: newSourceRequestEdges } =
          createNodesAndEdgesFromSchema(
            sourceRequestSchemaDynamic,
            "source-request",
            100,
            1,
            90,
            true,
            nodeYIncrement,
            sourceRootNodeX,
            false,
            true,
            sourceRequestRootNode ? sourceRequestRootNode.position.x : sourceResponseRootNode.position.x
          );

        newNodes.push(...newSourceRequestNodes);
        newEdges.push(...newSourceRequestEdges);
      }
    }

    // Generate Source Response Nodes and Edges
    if (sourceResponseSchema && sourceResponseSchema.properties) {
      const { nodes: newSourceResponseNodes, edges: newSourceResponseEdges } =
        createNodesAndEdgesFromSchema(
          sourceResponseSchema,
          "source-response", // Corrected: Parent is now "source"
          sourceRequestNodeXOffset,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          sourceResponseRootNode.position.x
        );

      newNodes.push(...newSourceResponseNodes);
      newEdges.push(...newSourceResponseEdges);
    }

    // Generate Destination Nodes and Edges
    if (destinationRequestSchema && destinationRequestSchema.properties) {
      const { nodes: newDestinationNodes, edges: newDestinationEdges } =
        createNodesAndEdgesFromSchema(
          destinationRequestSchema,
          "destination-request",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          destinationRootNode.position.x
        );
      newNodes.push(...newDestinationNodes);
      newEdges.push(...newDestinationEdges);
    }

    if (destinationResponseSchema && destinationResponseSchema.properties) {
      const { nodes: newDestinationResponseNodes, edges: newDestinationResponseEdges } =
        createNodesAndEdgesFromSchema(
          destinationResponseSchema,
          "destination-response",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          destinationResponseRootNode.position.x
        );
      newNodes.push(...newDestinationResponseNodes);
      newEdges.push(...newDestinationResponseEdges)
    }

    if (triggerResponseSchema && triggerResponseSchema.properties) {
      const { nodes: newTriggerResponseNodes, edges: newTriggerResponseEdges } =
        createNodesAndEdgesFromSchema(
          triggerResponseSchema,
          "trigger-response",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          triggerResponseRootNode.position.x
        );
      newNodes.push(...newTriggerResponseNodes);
      newEdges.push(...newTriggerResponseEdges)
    }

    // Load Existing Mappings
    if (dataMappings && dataMappings.length > 0) {
      dataMappings.forEach((mapping, index) => {
        //check if source node and target node already available in nodes
        if (newNodes.find(node => node.id === mapping.source.id) && newNodes.find(node => node.id === mapping.target.id)) {
          const { source, target, nodeLocation, dataTranslation, id } = mapping;
          const sourceNode = newNodes.find((node) => node.id === source.id);
          const targetNode = newNodes.find((node) => node.id === target.id);

          if (sourceNode && targetNode) {
            const { x, y } = nodeLocation;

            const newTranslationNodeId = id;

            const newTranslationNode = {
              id: newTranslationNodeId,
              type: "translateNode",
              data: {
                label: "Data Translation",
                dataTranslation: dataTranslation,
                onClick: handleOnDataTranslationClick,
              },
              position: { x, y },
            };
            newNodes.push(newTranslationNode);

            // Edge from Source to Data Translation
            newEdges.push({
              id: `e-${sourceNode.id}-${newTranslationNodeId}`,
              source: sourceNode.id,
              sourceHandle: "dt-a",
              target: newTranslationNodeId,
              targetHandle: "dt-b",
              type: "simplebezier",
              animated: true,
              style: { strokeWidth: 1, stroke: "#ffffff" },
            });

            // Edge from Data Translation to Target
            newEdges.push({
              id: `e-${newTranslationNodeId}-${targetNode.id}`,
              source: newTranslationNodeId,
              targetHandle: "dt-a",
              target: targetNode.id,
              type: "simplebezier",
              animated: true,
              style: { strokeWidth: 1, stroke: "#ffffff" },
            });
          }
        }
      });
    }

    setNodes(newNodes);
    setEdges(newEdges);

    if (interactive && reactFlowWrapper.current) {
      fitView({
        padding: 0.1,
        duration: 500,
        maxZoom: 2,
        minZoom: 0.5,
      });
    }
  }, [interactive, fitView, handleAddDataTranslation, dataMappings, triggerRequestSchema, sourceRequestSchema, sourceResponseSchema, destinationRequestSchema, destinationResponseSchema, triggerResponseSchema]);

  // Close sidebar handler
  const handleCloseSidebar = () => {
    setShowSidebar(false);
    setSelectedNode(null);
  };

  const deleteDataTranslation = (node) => {
    if (!reactFlowInstance.current) return; // Check if reactFlowInstance is available

    const allConnections = updateConnections(reactFlowInstance, selectedNode).filter(connection => connection.id !== selectedNode.id);

    // console.log(allConnections);

    dispatch(updateIntegrationMappingDetails({ key: 'mappingDetails', value: allConnections }));

    handleCloseSidebar();

  }

  const onApplyDataTranslation = () => {
    if (!reactFlowInstance.current) return; // Check if reactFlowInstance is available

    console.log(selectedNode);


    const allConnections = updateConnections(reactFlowInstance, selectedNode);

    dispatch(updateIntegrationMappingDetails({ key: 'mappingDetails', value: allConnections }));


    handleCloseSidebar();
  }

  const updateConnections = (reactFlowInstance, selectedNode = null) => {
    const allConnections = [];

    const translationNodes = reactFlowInstance.current.getNodes().filter(node => node.type === "translateNode");
    translationNodes.forEach(translationNode => {
      const connectedEdges = reactFlowInstance.current.getEdges().filter(edge => edge.source === translationNode.id || edge.target === translationNode.id);

      if (connectedEdges.length === 2) {
        const sourceEdge = connectedEdges.find(edge => edge.target === translationNode.id);
        const targetEdge = connectedEdges.find(edge => edge.source === translationNode.id);

        const sourceNode = reactFlowInstance.current.getNodes().find(node => node.id === sourceEdge.source);
        const destinationNode = reactFlowInstance.current.getNodes().find(node => node.id === targetEdge.target);

        const connectedNodesDetails = {
          id: translationNode.id,
          source: {
            id: sourceNode?.id,
            label: sourceNode?.data?.label || "Root",
            data: sourceNode?.data,
          },
          target: {
            id: destinationNode?.id,
            label: destinationNode?.data?.label || "Root",
            data: destinationNode?.data,
          },
          nodeLocation: {
            x: translationNode.position.x,
            y: translationNode.position.y
          }
        };

        // Merge dataTranslation if source and target IDs match
        if (selectedNode &&
          selectedNode.source.id === sourceNode.id &&
          selectedNode.target.id === destinationNode.id) {
          connectedNodesDetails.dataTranslation = selectedNode.dataTranslation;
        } else if (translationNode && translationNode.data && translationNode.data.dataTranslation) {
          connectedNodesDetails.dataTranslation = translationNode.data.dataTranslation;
        }

        allConnections.push(connectedNodesDetails);
      } else {
        allConnections.push(
          {
            id: translationNode.id,
            source: {
              id: null,
              label: "Root",
              data: null,
            },
            target: {
              id: null,
              label: "Root",
              data: null,
            },
            nodeLocation: {
              x: translationNode.position.x,
              y: translationNode.position.y
            },
            dataTranslation: {}
          }
        );
      }

    });
    return allConnections;
  }

  const updateConnectedNodesDetails = (clickedNode) => {
    console.log("updateConnectedNodesDetails clickedNode:", clickedNode);
    const connectedEdges = getEdges().filter(
      (edge) => edge.source === clickedNode.id || edge.target === clickedNode.id
    );

    console.log("updateConnectedNodesDetails connectedEdges:", connectedEdges);

    let sourceNode = null;
    let destinationNode = null;

    if (clickedNode.type === "translateNode") {
      // Find the edge where the translateNode is the target (incoming edge)
      const incomingEdge = connectedEdges.find((edge) => edge.target === clickedNode.id);
      // Find the edge where the translateNode is the source (outgoing edge)
      const outgoingEdge = connectedEdges.find((edge) => edge.source === clickedNode.id);

      if (incomingEdge) {
        sourceNode = getNodes().find((node) => node.id === incomingEdge.source);
      }
      if (outgoingEdge) {
        destinationNode = getNodes().find((node) => node.id === outgoingEdge.target);
      }
    } else {
      const edge = connectedEdges[0];
      if (edge) {
        if (edge.source === clickedNode.id) {
          destinationNode = getNodes().find((node) => node.id === edge.target);
        } else if (edge.target === clickedNode.id) {
          sourceNode = getNodes().find((node) => node.id === edge.source);
        }
      }
    }

    console.log("updateConnectedNodesDetails sourceNode:", sourceNode);
    console.log("updateConnectedNodesDetails destinationNode:", destinationNode);

    const connectedNodesDetails = {
      id: clickedNode.id,
      source: {
        id: sourceNode?.id,
        label: sourceNode?.data?.label || "Root",
        data: sourceNode?.data,
      },
      target: {
        id: destinationNode?.id,
        label: destinationNode?.data?.label || "Root",
        data: destinationNode?.data,
      },
      dataTranslation: clickedNode.data.dataTranslation,
    };
    console.log(
      "updateConnectedNodesDetails connectedNodesDetails:",
      connectedNodesDetails
    );
    return connectedNodesDetails;
  };


  return (
    <div className="relative h-[calc(100vh-190px)]">
      {/* Sidebar */}
      {showSidebar && (
        <div className="absolute right-0 top-0 z-10 h-full w-[33%] overflow-auto rounded-md border border-gray-200 bg-theme-primary p-4">
          <DataTranslationForm selectedNode={selectedNode} />
          <div className="absolute bottom-0 right-4 flex gap-2">
            <button
              onClick={handleCloseSidebar}
              className="mb-4 rounded-md bg-gray-500 p-2 text-gray-900 hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteDataTranslation(selectedNode)}
              className="mb-4 rounded-md bg-red-400 p-2 text-gray-900 hover:bg-red-500"
            >
              Delete
            </button>
            <button
              onClick={onApplyDataTranslation}
              className="mb-4 rounded-md bg-blue-400 p-2 text-gray-900 hover:bg-blue-600"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Fixed "Add Data Translation" Button */}
      <div className="absolute left-4 top-4 z-20">
        <DataTranslationButtonNode
          data={{
            label: "Add Data Translation",
            onClick: handleAddDataTranslation,
          }}
        />
      </div>

      <ReactFlow
        className="inner-strong-shadow rounded-md bg-white-500"
        nodeTypes={nodeTypes}
        style={{ width: "100%", height: "100%" }}
        nodes={nodes}
        edges={edges}
        elementsSelectable={true}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        connectionLineType="simplebezier"
        connectionLineStyle={{ strokeWidth: 1, stroke: "#ffffff" }}
        isValidConnection={isValidConnection}
        defaultEdgeOptions={{
          type: "simplebezier",
          style: { strokeWidth: 1, stroke: "#ffffff" },
        }}
        fitViewOptions={{
          padding: 0.1,
          duration: 500,
          maxZoom: 2,
          minZoom: 0.5,
        }}
        onInit={onReactFlowInit}
      >
        <Controls />
        <Background />
      </ReactFlow>
    </div>
  );
};

export default DataMappingDiagram;
